import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck, Lock, User, ArrowRight } from 'lucide-react';
import { auth, ApiError } from '@/api/client';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';

/**
 * /auth/accept-invite?iid=...&t=...   site invitation
 * /auth/accept-invite?tid=...&t=...   platform-tier account setup
 *
 * Invited user lands here from the email link. They set a password and
 * confirm their full name. The backend activates the account; the user
 * then proceeds to /login to actually sign in.
 *
 * Platform-tier users (admin/createUser) have no site, so their email carries
 * a password-reset token (`tid`) instead of an invitation (`iid`). Same
 * first-time screen; it submits to /auth/reset-password instead.
 */

interface AcceptResponse {
  /** Absent for account setup — /auth/reset-password doesn't return it. */
  role?: string;
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate  = useNavigate();
  const inviteId  = params.get('iid') ?? '';
  const setupTokenId = params.get('tid') ?? '';
  const token     = params.get('t')   ?? '';
  const linkId    = inviteId || setupTokenId;

  const [fullName, setFullName]         = useState('');
  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [policyMet, setPolicyMet]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [result, setResult]             = useState<AcceptResponse | null>(null);

  useEffect(() => {
    if (!linkId || !token) setError('This invitation link is invalid or incomplete.');
  }, [linkId, token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const name = fullName.trim() || undefined;
      if (inviteId) {
        setResult(await auth.acceptInvite(inviteId, token, password, name));
      } else {
        await auth.resetPassword(setupTokenId, token, password, name);
        setResult({});
      }
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMITED') {
          setError('Too many attempts. Please wait before trying again.');
        } else if (err.code === 'VALIDATION_ERROR') {
          const msg = err.message ?? '';
          if (msg.toLowerCase().includes('expired')) {
            navigate('/login', {
              state: {
                notice: inviteId
                  ? 'Your invitation link has expired. Ask your site admin to resend the invite.'
                  : 'Your account setup link has expired. Use "Forgot password" below to get a new one.',
              },
            });
            return;
          }
          const details = err.details as { errors?: string[] } | null;
          const policyErrors = details?.errors;
          setError(policyErrors?.length ? policyErrors.join(' ') : msg);
        } else {
          setError(err.message || 'Account activation failed. Please try again.');
        }
      } else {
        setError('Account activation failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title={<>Activate your<br/>account</>}
      lede="You've been invited to join the DreamTrials Clinical Trial Management System."
    >
      {result ? (
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-navy-50 grid place-items-center">
            <ShieldCheck size={24} className="text-navy"/>
          </div>
          <p className="eyebrow mb-2">Activated</p>
          <h1 className="text-[24px] font-extrabold text-text-1 mb-2 tracking-tight">You're set up</h1>
          <p className="text-[12.5px] text-text-3 leading-relaxed mb-1">
            {result.role
              ? <>Welcome to DreamTrials as <strong className="text-text-1">{result.role}</strong>.</>
              : <>Welcome to DreamTrials.</>}
          </p>
          <p className="text-[12.5px] text-text-3 leading-relaxed">Redirecting to sign in…</p>
        </div>
      ) : (
        <>
          <p className="eyebrow mb-2">Account setup</p>
          <h1 className="text-[26px] font-extrabold text-text-1 mb-1.5 tracking-tight">Welcome</h1>
          <p className="text-[12.5px] text-text-3 mb-6 leading-relaxed">
            Set your password to activate your account.
          </p>

          <div className="relative bg-white border border-border rounded-sm p-7 pb-6 shadow-card">
            <HipaaBadge />
            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-[18px]">
                <label htmlFor="full_name" className="label text-[10px] tracking-wider mb-2">Full Name</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <User size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="full_name" type="text" value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Jane Smith"
                    autoComplete="name" maxLength={60}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                </div>
              </div>

              <div className="mb-1.5">
                <label htmlFor="password" className="label text-[10px] tracking-wider mb-2">New Password</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <Lock size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 12 characters"
                    autoComplete="new-password" required minLength={12} maxLength={20}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                  <button
                    type="button" onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="p-1 text-text-4 hover:text-text-2"
                  >
                    {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>

              <div className="mb-[18px]">
                <PasswordStrengthMeter password={password} userInputs={[fullName, linkId]} onPolicyChange={setPolicyMet} />
              </div>

              <div className="mb-[18px]">
                <label htmlFor="confirm" className="label text-[10px] tracking-wider mb-2">Confirm Password</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <Lock size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="confirm" type={showPassword ? 'text' : 'password'} value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    autoComplete="new-password" required minLength={12} maxLength={20}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                </div>
              </div>

              <div className="h-[46px] flex items-center mb-3.5">
                {error && <AuthErrorBanner message={error} />}
              </div>

              <button
                type="submit" disabled={submitting || !linkId || !token || !policyMet}
                className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-65 disabled:cursor-not-allowed mb-4"
              >
                {submitting
                  ? <><Loader2 size={14} className="animate-spin"/> Activating…</>
                  : <>Activate account <ArrowRight size={14} className="opacity-75" /></>}
              </button>

              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline">
                  <ArrowLeft size={13}/> Back to sign in
                </Link>
              </div>
            </form>
          </div>
        </>
      )}
    </AuthShell>
  );
}
